import {Box, HStack, Spacer, VStack} from '@chakra-ui/react';
import {LignePanierRemplacementPicto} from '@/domains/ligneProduit/LignePanierRemplacementPicto';
import {ArticleBlock} from '../ArticleBlock';
import {PrixBlock} from '../PrixBlock';
import {useTranslation} from 'react-i18next';
import React from 'react';
import {useHalState} from '@synako/haligator';
import {useBuyingBlocks} from '@/hooks/panier/useBuyingBlocks';
import {LigneProduitLayout} from '@/domains/liste/components/LigneProduitLayout';
import {LigneProduitCommune} from '@/domains/ligneProduit/LigneProduitCommune';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {
  ItemCatalogue2StateFF,
  ItemCatalogue2StateMarketplace,
  LigneDePanierCommands,
  isItemStateCatalogueFF,
  isItemStateCatalogueMarketplace,
  useLigneDePanierCommands,
  useLivraisonsPossiblesContext,
} from 'ff-common';
import {ArticleBlockMarketplace} from '../ArticleBlockMarketplace';
import {BuyProductPanierInput} from '@/domains/panier/BuyProduct/BuyProductPanierInput';
import {State} from '@synako/halitrek';
import {LigneMatching, LigneProblem} from 'api-types/ffconnect';
import {Warning} from './Warning';
import {MatchingLignePanier} from '../MatchingLignePanier';
import {DeleteButton} from '@/domains/ligneProduit/DeleteButton';
import {RemplacerLigneButton} from './RemplacerLigneButton';
import isEqual from 'react-fast-compare';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useEditionPrixState} from '@/hooks/panier/usePriceEditor';
import {ModaleErreurDisponibilite} from '@/domains/cadencier/components/ModaleErreurDisponibilite.tsx';
import {useMarketplaceBrandColors} from '@/hooks/marketplace/useMarketplaceBrandColors';
import {usePeutModifierPrix} from '@/hooks/auth/usePermissionsCommerciales';

export const ProduitPanierAffiche: React.FC<{
  ligneDePanier: State<LigneDePanierDesc>;
  idError: string;
  probleme: LigneProblem | undefined;
  matching?: LigneMatching;
  /** Panier PDF : affiche Remplacer à droite de la corbeille. */
  afficherRemplacer?: boolean;
}> = React.memo(
  ({ligneDePanier, idError, probleme, matching, afficherRemplacer}) => {
    const {t} = useTranslation();

    const remplacement_de = ligneDePanier.data.remplacement_de;
    const textToast = useSimpleTextToast();
    const onError = useDefaultOnError(textToast);
    const item = useHalState(ligneDePanier.follow('item_2'));
    const itemStateFF =
      item.state && isItemStateCatalogueFF(item.state) ? item.state : undefined;
    const itemStateMarketplace =
      item.state && isItemStateCatalogueMarketplace(item.state)
        ? item.state
        : undefined;
    const {setSelectedMarketplaceId} = useLivraisonsPossiblesContext();
    const commands = useLigneDePanierCommands(
      ligneDePanier,
      textToast,
      onError,
      ModaleErreurDisponibilite,
      undefined,
      undefined,
      undefined,
      itemStateMarketplace
        ? {
            onChangerLaQuantiteSuccess: () => {
              setSelectedMarketplaceId(
                itemStateMarketplace.data.marketplace_id,
              );
            },
          }
        : undefined,
    );
    const {somethingSubmitting, supprimer} = commands;

    const handleDelete = () => {
      supprimer.submit({});
    };

    // Remplacer : panier PDF + commande HAL + article pour peupler la modale.
    const peutRemplacer =
      Boolean(afficherRemplacer) &&
      commands.remplacer.isAvailable &&
      ligneDePanier.hasOneLink('article');

    // Sous la ligne dans le panier : l'alerte de validation d'abord, puis la synthèse du
    // matching sur un panier PDF — les deux peuvent coexister (un produit du
    // bon de commande peut être en rupture). `undefined` => quand il n'y a rien
    const footerBlock =
      probleme || matching ? (
        <>
          {probleme && (
            <Warning
              idError={idError}
              ligneDePanier={ligneDePanier}
              probleme={probleme}
              commands={commands}
            />
          )}
          {matching && (
            <MatchingLignePanier
              ligneMatching={matching}
              estRemplacee={Boolean(remplacement_de)}
            />
          )}
        </>
      ) : undefined;

    return (
      <Box
        position='relative'
        w='100%'
        borderBottom='1px solid'
        borderColor='gray.light'
        _last={{borderBottom: '0'}}
      >
        {remplacement_de && (
          <LignePanierRemplacementPicto
            remplacement_de={remplacement_de}
            t={t}
          />
        )}
        {itemStateMarketplace ? (
          <LigneMarketplacePanier
            ligneDePanier={ligneDePanier}
            itemState={itemStateMarketplace}
            commands={commands}
            onDelete={handleDelete}
            footerBlock={footerBlock}
          />
        ) : itemStateFF ? (
          <LigneFFPanier
            ligneDePanier={ligneDePanier}
            itemState={itemStateFF}
            commands={commands}
            onDelete={handleDelete}
            somethingSubmitting={somethingSubmitting}
            footerBlock={footerBlock}
            peutRemplacer={peutRemplacer}
            forcerBoutonDlcStock={Boolean(afficherRemplacer)}
          />
        ) : item.error ? (
          <LigneHorsCataloguePanier
            ligneDePanier={ligneDePanier}
            commands={commands}
            onDelete={handleDelete}
            somethingSubmitting={somethingSubmitting}
            footerBlock={footerBlock}
            peutRemplacer={peutRemplacer}
          />
        ) : null}
      </Box>
    );
  },
  function propsAreEqual(prevProps, nextProps) {
    const same =
      isEqual(prevProps.ligneDePanier, nextProps.ligneDePanier) &&
      isEqual(prevProps.probleme, nextProps.probleme) &&
      isEqual(prevProps.matching, nextProps.matching) &&
      prevProps.afficherRemplacer === nextProps.afficherRemplacer;
    return same;
  },
);

const LigneFFPanier: React.FC<{
  ligneDePanier: State<LigneDePanierDesc>;
  itemState: ItemCatalogue2StateFF;
  commands: LigneDePanierCommands;
  onDelete: () => void;
  somethingSubmitting: boolean;
  footerBlock?: React.ReactNode;
  peutRemplacer?: boolean;
  forcerBoutonDlcStock?: boolean;
}> = ({
  ligneDePanier,
  itemState,
  commands,
  onDelete,
  somethingSubmitting,
  footerBlock,
  peutRemplacer,
  forcerBoutonDlcStock,
}) => {
  const textToast = useSimpleTextToast();
  const hasArticleLink = ligneDePanier.hasOneLink('article');
  const article = useHalState(
    hasArticleLink ? ligneDePanier.follow('article') : undefined,
  );
  const {uniteQuantiteBlock, buyBlock} = useBuyingBlocks(
    textToast,
    'panier',
    itemState,
    ligneDePanier,
  );
  const peutModifierPrix = usePeutModifierPrix();
  const {prixDraft, onEditionPrixChange} = useEditionPrixState();

  const articleBlock = (
    <ArticleBlock
      code_article={ligneDePanier.data.code_article}
      dispo={ligneDePanier.follow('disponibilite_article')}
      libelleFallback={ligneDePanier.data.libelle}
      libelles_tronques
      ligneDePanier={ligneDePanier}
      afficherTagCadencier
      prixDraft={prixDraft}
      forcerBoutonDlcStock={forcerBoutonDlcStock}
    />
  );
  const prixBlock = (
    <PrixBlock
      refreshing={somethingSubmitting}
      ligneDePanier={ligneDePanier}
      onEditionPrixChange={onEditionPrixChange}
      ml='0'
      mr={{base: 0, md: 1, '2xl': 2}}
      minW={{base: 0, md: peutModifierPrix ? '155px' : '120px', '2xl': '130px'}}
    />
  );
  const deleteBlock = (
    <HStack
      spacing='0'
      alignItems='center'
    >
      <DeleteButton
        onClick={onDelete}
        isLoading={somethingSubmitting}
      />
      {peutRemplacer && (
        <RemplacerLigneButton
          ligneDePanier={ligneDePanier}
          remplacerSubmit={commands.remplacer.submit}
          isLoading={somethingSubmitting}
        />
      )}
    </HStack>
  );

  return (
    <LigneProduitCommune
      itemState={itemState}
      articleBlock={articleBlock}
      prixBlock={prixBlock}
      uniteQuantiteBlock={uniteQuantiteBlock}
      aPeser={article.data?.tolede.a_peser}
      buyBlock={buyBlock}
      somethingSubmitting={somethingSubmitting}
      deleteBlock={deleteBlock}
      ligneDePanier={ligneDePanier}
      footerBlock={footerBlock}
      compactDesktop
    />
  );
};

// Ligne dont l'item du catalogue ne se charge plus (article sorti du catalogue du client) :
// ni prix ni quantité, seulement de quoi la reconnaître et la supprimer
const LigneHorsCataloguePanier: React.FC<{
  ligneDePanier: State<LigneDePanierDesc>;
  commands: LigneDePanierCommands;
  onDelete: () => void;
  somethingSubmitting: boolean;
  footerBlock?: React.ReactNode;
  peutRemplacer?: boolean;
}> = ({
  ligneDePanier,
  commands,
  onDelete,
  somethingSubmitting,
  footerBlock,
  peutRemplacer,
}) => {
  return (
    <LigneProduitLayout
      flexDirection='column'
      px='0'
    >
      <HStack
        width='100%'
        alignItems='start'
      >
        <Box
          flex='1'
          minW={0}
        >
          <ArticleBlock
            code_article={ligneDePanier.data.code_article}
            dispo={ligneDePanier.follow('disponibilite_article')}
            libelleFallback={ligneDePanier.data.libelle}
            articleFallback={
              ligneDePanier.hasOneLink('article')
                ? ligneDePanier.follow('article')
                : undefined
            }
            libelles_tronques
          />
        </Box>
        <HStack
          spacing='0'
          alignItems='center'
        >
          <DeleteButton
            onClick={onDelete}
            isLoading={somethingSubmitting}
          />
          {peutRemplacer && (
            <RemplacerLigneButton
              ligneDePanier={ligneDePanier}
              remplacerSubmit={commands.remplacer.submit}
              isLoading={somethingSubmitting}
            />
          )}
        </HStack>
      </HStack>
      {footerBlock && <Box w='100%'>{footerBlock}</Box>}
    </LigneProduitLayout>
  );
};

const LigneMarketplacePanier: React.FC<{
  ligneDePanier: State<LigneDePanierDesc>;
  itemState: ItemCatalogue2StateMarketplace;
  commands: LigneDePanierCommands;
  onDelete: () => void;
  footerBlock?: React.ReactNode;
}> = ({ligneDePanier, itemState, commands, onDelete, footerBlock}) => {
  const {somethingSubmitting, changerLaQuantite} = commands;
  const {couleur} = useMarketplaceBrandColors(itemState.data.marketplace_id);

  const articleBlock = <ArticleBlockMarketplace itemState={itemState} />;
  const prixBlock = (
    <PrixBlock
      refreshing={somethingSubmitting}
      ligneDePanier={ligneDePanier}
    />
  );
  const selectBlock = (
    <Box
      mt='15px'
      ml={{base: 0, md: 'auto'}}
      minW='245px'
      w={{base: '100%', lg: '245px'}}
    >
      <BuyProductPanierInput
        itemCatalogue={itemState}
        quantite={ligneDePanier.data.quantite}
        commande_en='unitaire'
        somethingSubmitting={somethingSubmitting}
        context='panier'
        primaryColor={couleur}
        onQuantite={(q) => {
          changerLaQuantite.submit({quantite: q});
        }}
      />
    </Box>
  );
  const deleteBlock = (
    <DeleteButton
      onClick={onDelete}
      isLoading={somethingSubmitting}
    />
  );

  return (
    <LigneProduitLayout
      flexDirection='column'
      px='0'
    >
      {/* Desktop */}
      <HStack
        width='100%'
        alignItems='start'
        display={{base: 'none', md: 'flex'}}
        flexWrap='wrap'
        gap='0'
        pt='0px'
        flex='1'
      >
        <VStack>{articleBlock}</VStack>
        <Spacer />
        {prixBlock}
        <HStack
          justifyContent='end'
          alignItems='start'
          w='100%'
        >
          {selectBlock}
          <Box mt='15px'>{deleteBlock}</Box>
        </HStack>
      </HStack>
      {/* Mobile */}
      <VStack
        width='100%'
        alignItems='stretch'
        display={{base: 'flex', md: 'none'}}
        position='relative'
      >
        <Box
          position='absolute'
          top='0'
          right='0'
          zIndex='1'
        >
          {deleteBlock}
        </Box>
        {articleBlock}
        <HStack justify='space-between'>
          {selectBlock}
          {prixBlock}
        </HStack>
      </VStack>
      {footerBlock && <Box w='100%'>{footerBlock}</Box>}
    </LigneProduitLayout>
  );
};
